/**
 * The searches of a pass, shaped for the dialog (CU-869fmm943).
 *
 * One item per search the pass launched (webback
 * `GET /executions/<id>/searches`): a search is one office and one pick-up
 * date, with all its sites and rental lengths. Pure functions — the
 * dialog only lays them out, so what the user reads is tested here.
 *
 * `canon` folds legacy event names (`canonicalEventType` of Activity);
 * it is a parameter so this file stays importable by `node --test`.
 */
import { fmtInt } from '../../Page/@services/cadence.js'
import { passProgress } from './passCounts.js'

const same = (t) => t
// Largest first; a tie reads in alphabetical order, so the chips never reshuffle.
const byCount = (a, b) => (b[1] - a[1]) || String(a[0]).localeCompare(String(b[0]))

/** "3 · 5 · 7 days"; "—" when the search's NEW_SEARCH rows are gone. */
export function lengthsLabel(lors) {
	if (!Array.isArray(lors) || !lors.length) return '—'
	const sorted = [...lors].sort((a, b) => a - b)
	return `${sorted.join(' · ')} ${sorted.length === 1 && sorted[0] === 1 ? 'day' : 'days'}`
}

/** One name and "+N" (UX Obs. 17); `all` is the tooltip. Null when unknown. */
export function sitesPreview(sites) {
	if (!Array.isArray(sites)) return null
	if (!sites.length) return { first: null, more: 0, all: '' }
	return { first: sites[0].name, more: sites.length - 1, all: sites.map(s => s.name).join(', ') }
}

/** Criterion 4: the sentence when fewer searches are listed than the pass composed. */
export function gapNote(composed, listed) {
	const c = Number(composed) || 0
	const l = Number(listed) || 0
	if (!l || c <= l) return null
	const missing = c - l
	return `${fmtInt(l)} of the ${fmtInt(c)} searches of this pass are listed. The other ${fmtInt(missing)} `
		+ `${missing === 1 ? 'was' : 'were'} asked for but never launched — the pass's detail says why, under Not searched.`
}

/** The pass row's progress format for one search; null when there is nothing to count. */
export function rowProgress(item) {
	if (!item?.progress) return null
	return passProgress({ searches: item.progress, created: item.push?.tasks_expected })
}

/** `[[kind, n]]` of one search, legacy names folded, largest first. */
export function eventKinds(events, canon = same) {
	const out = {}
	for (const [type, n] of Object.entries(events || {})) {
		const k = canon(type)
		out[k] = (out[k] || 0) + (Number(n) || 0)
	}
	return Object.entries(out).filter(([, n]) => n > 0).sort(byCount)
}

/** The kinds this pass has, with their totals, largest first — the filter's options. */
export function kindOptions(items, canon = same) {
	const totals = {}
	for (const item of items || []) {
		for (const [k, n] of eventKinds(item.events, canon)) totals[k] = (totals[k] || 0) + n
	}
	return Object.entries(totals).sort(byCount).map(([kind, n]) => ({ kind, n }))
}

/** A search stays when it has ≥ 1 event of any chosen kind; no kinds = every search. */
export function filterByKinds(items, kinds, canon = same) {
	if (!kinds || !kinds.length) return items || []
	const wanted = new Set(kinds)
	return (items || []).filter(item => eventKinds(item.events, canon).some(([k]) => wanted.has(k)))
}

/** "4 of 340 searches", beside the field when a filter is on. */
export function countLabel(shown, total) {
	return `${fmtInt(shown)} of ${fmtInt(total)} ${total === 1 ? 'search' : 'searches'}`
}

/** What the table's search box matches: the office (name and code) and every site. */
export function searchText(item) {
	return [item?.office?.name, item?.office?.code, ...(item?.sites || []).map(s => s.name)]
		.filter(Boolean).join(' ')
}

export function officeLabel(office) {
	return { name: office?.name || '—', code: office?.code || null }
}
